import React, { useState, useEffect, useMemo } from 'react'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { 
  Box, 
  Button, 
  TextField, 
  Snackbar, 
  Alert, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControlLabel,
  Typography,
  Card,
  CardContent,
} from '@mui/material'
import { Edit as EditIcon, Visibility as VisibilityIcon } from '@mui/icons-material'
import { formatNumber } from '@auteco/utils/formatsNumber'
import { modificarContenedores, modificarSellInDirecto, modificarSellInTecnoSuper, modificarSellOutTecnoSuper, getInventarioComentarios, putAsignarComentario, getInclusionLlegadasModelo, modificarLicitacion, sellInMultiple, sellInTecnoSuperMultiple, licitacionMultiple, sellOutTecnoSuperMultiple } from '../../services/motosSnopServices'
import {
  esCompaniaElectric,
  FILAS_ELECTRIC,
  getFilasVisiblesPorDefecto,
  getTodasLasFilasPlanificacion,
  normalizarNombreFila,
} from '../../constants/compraPlanificacionFilas'
import './planificacionTable.css'

// Configuración de los tipos de dato que admiten edición masiva (múltiple) por periodos.
// valueKey: nombre del campo que espera el endpoint; service: función del servicio a invocar.
const MULTIPLE_EDIT_CONFIG = {
  'Sell In -- Plan Ventas': { title: 'Sell in múltiple', label: 'sell in', valueKey: 'sell_in', service: sellInMultiple },
  'Sell In Tecnosuper': { title: 'Sell In Tecnosuper múltiple', label: 'sell in tecno super', valueKey: 'sell_in_tecnosuper', service: sellInTecnoSuperMultiple },
  'Licitacion': { title: 'Licitación múltiple', label: 'licitación', valueKey: 'licitacion', service: licitacionMultiple },
  'Sell Out Tecnosuper': { title: 'Sell Out Tecnosuper múltiple', label: 'sell out tecno super', valueKey: 'sell_out_tecnosuper', service: sellOutTecnoSuperMultiple },
}



const formatDecimalMax2 = (value) => {
  const num = Number(value)
  if (Number.isNaN(num)) return null
  return num.toLocaleString('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

const getBiasMapeParts = (unidades, ratio) => {
  const tieneUnidades = unidades !== undefined && unidades !== null && unidades !== ''
  const parsedUnidades = Number(unidades)
  const u =
    tieneUnidades && !Number.isNaN(parsedUnidades)
      ? formatNumber(parsedUnidades)
      : tieneUnidades
        ? String(unidades)
        : '—'
  const r =
    ratio !== undefined && ratio !== null && ratio !== '' && !Number.isNaN(Number(ratio))
  const pctStr = r ? `${Math.floor(Number(ratio) * 100)}%` : null
  return { u, pctStr }
}

/** Tarjetas compactas alineadas con el estilo de InventarioCards */
const BiasMapeMiniCard = ({ title, unidades, ratio, variant = 'bias' }) => {
  const { u, pctStr } = getBiasMapeParts(unidades, ratio)
  const palette =
    variant === 'mape'
      ? {
        bg: 'linear-gradient(145deg, #e3f2fd 0%, #d6e9f8 100%)',
          border: 'rgba(103, 58, 183, 0.22)',
          titleColor: '#1565c0',
          valueShadow: '0 1px 0 rgba(255,255,255,0.8)',
        }
      : {
          bg: 'linear-gradient(145deg, #e3f2fd 0%, #d6e9f8 100%)',
          border: 'rgba(25, 118, 210, 0.22)',
          titleColor: '#1565c0',
          valueShadow: '0 1px 0 rgba(255,255,255,0.85)',
        }

  return (
    <Card
      elevation={0}
      sx={{
        minWidth: 112,
        maxWidth: 140,
        border: '1px solid',
        borderColor: palette.border,
        borderRadius: 2,
        background: palette.bg,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
        transition: 'box-shadow 0.2s ease, transform 0.15s ease',
        '&:hover': {
          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      <CardContent
        sx={{
          py: 1.25,
          px: 1.75,
          textAlign: 'center',
          '&:last-child': { pb: 1.25 },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 600,
            fontSize: '0.7rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: palette.titleColor,
            mb: 0.75,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'center',
            gap: 0.35,
            flexWrap: 'wrap',
            textShadow: palette.valueShadow,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontWeight: 800,
              fontSize: '1.125rem',
              fontVariantNumeric: 'tabular-nums',
              color: 'rgba(0, 0, 0, 0.87)',
              lineHeight: 1.2,
            }}
          >
            {u}
          </Typography>
          {pctStr ? (
            <Typography
              component="span"
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                color: 'text.secondary',
                lineHeight: 1.2,
              }}
            >
              ({pctStr})
            </Typography>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  )
}

const PlanificacionTable = ({ 
  tablaData, 
  mesesColumnas, 
  onSellInButtonClick,
  onLlegadasButtonClick,
  tipoPeriodo = 'mes',
  mesLlegada = '',
  fobLlegadasUnidadesTotales = null,
  modeloPlaneacion = '',
  escenario = 'inicial_2025_6',
  onDataRefresh,
  nivelServicio,
  readOnly = false,
  tiposPeriodoDisponibles,
  onTipoPeriodoChange,
  metricasBiasMape,
  tipoNegocio = '',
  mostrarKardexTecnoSuper = null,
}) => {
  const [editingCell, setEditingCell] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [updating, setUpdating] = useState(false)
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' })
  const [comentarios, setComentarios] = useState([])
  const [editingComentario, setEditingComentario] = useState(null) // Para controlar qué celda de comentario se está editando
  const [comentarioValue, setComentarioValue] = useState('') // Valor del comentario siendo editado
  const [isInclusionLlegadas, setIsInclusionLlegadas] = useState(0) // Estado para controlar si se incluyen llegadas
  const [openMultipleModal, setOpenMultipleModal] = useState(false)
  const [multipleTipoDato, setMultipleTipoDato] = useState('') // tipoDato que abrió el modal de edición masiva
  const [multipleValue, setMultipleValue] = useState('')
  const [multiplePeriodos, setMultiplePeriodos] = useState([]) // { anio, mes_semana, label, selected }
  const [openConfirmacionMultiple, setOpenConfirmacionMultiple] = useState(false)
  const [submittingMultiple, setSubmittingMultiple] = useState(false)
  
  const todasLasFilas = useMemo(() => getTodasLasFilasPlanificacion(), [])
  const filasElectricLabels = useMemo(
    () => FILAS_ELECTRIC.map((fila) => fila.label),
    [],
  )

  // Función para obtener mes y año de una columna
  const getMesAnoFromColumn = (mesColumna) => {
    if (tipoPeriodo === 'mes') {
      const nombresMeses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
      const [nombreMes, añoCorto] = mesColumna.split('-')
      const mes = nombresMeses.indexOf(nombreMes) + 1
      const año = parseInt(`20${añoCorto}`, 10)
      return { mes, año }
    } 
    if (tipoPeriodo === 'semana') {
      const [semanaStr, añoStr] = mesColumna.split('-')
      const semana = parseInt(semanaStr, 10)
      const año = parseInt(añoStr, 10)
      // Para semanas, usamos la semana como "mes" en el contexto de la API
      return { mes: semana, año }
    }
    return { mes: 0, año: 0 }
  }

  // Periodos desde mes actual hasta ese mes del año siguiente (para Sell in múltiple)
  const getPeriodosMesActualHastaSiguienteAnio = () => {
    const nombresMeses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
    const ahora = new Date()
    const mesActual = ahora.getMonth() + 1
    const añoActual = ahora.getFullYear()
    const periodos = []
    let mes = mesActual
    let año = añoActual
    const mesFin = mesActual
    const añoFin = añoActual + 1
    do {
      periodos.push({
        anio: año,
        mes_semana: mes,
        label: `${nombresMeses[mes - 1]}-${año}`,
        selected: true,
      })
      mes += 1
      if (mes > 12) {
        mes = 1
        año += 1
      }
    } while (año < añoFin || (año === añoFin && mes <= mesFin))
    return periodos
  }

  // Función para iniciar edición de comentario
  const handleEditComentarioStart = (mesColumna, comentarioActual) => {
    setEditingComentario(mesColumna)
    setComentarioValue(comentarioActual || '')
  }

  // Función para cancelar edición de comentario
  const handleEditComentarioCancel = () => {
    setEditingComentario(null)
    setComentarioValue('')
  }

  // Función para guardar comentario
  const handleSaveComentario = async (mesColumna) => {
    if (!comentarioValue.trim()) {
      setAlert({ open: true, message: 'El comentario no puede estar vacío', severity: 'warning' })
      return
    }

    setUpdating(true)
    try {
      const { mes, año } = getMesAnoFromColumn(mesColumna)
      
      const response = await putAsignarComentario({
        modelo_planeacion: modeloPlaneacion,
        escenario,
        comentario: comentarioValue.trim(),
        anio: año,
        mes_semana: mes,
      })

      if (response && response.success === 1) {
        // Actualizar el estado de comentarios con el nuevo comentario
        const nuevoComentario = {
          modelo_planeacion: modeloPlaneacion,
          escenario,
          fecha: new Date().toISOString(),
          mes_semana: mes,
          comentario: comentarioValue.trim(),
          anio: año
        }
        
        // Actualizar o agregar el comentario en el estado
        setComentarios(prevComentarios => {
          const comentarioExistente = prevComentarios.findIndex(
            c => c.mes_semana === mes && c.anio === año
          )
          
          if (comentarioExistente !== -1) {
            // Actualizar comentario existente
            const nuevosComentarios = [...prevComentarios]
            nuevosComentarios[comentarioExistente] = nuevoComentario
            return nuevosComentarios
          }
          // Agregar nuevo comentario
          return [...prevComentarios, nuevoComentario]
        })

        setAlert({ 
          open: true, 
          message: 'Comentario guardado exitosamente', 
          severity: 'success' 
        })
        setEditingComentario(null)
        setComentarioValue('')
      } else {
        setAlert({ 
          open: true, 
          message: 'Error al guardar el comentario', 
          severity: 'error' 
        })
      }
    } catch (error) {
      console.error('Error guardando comentario:', error)
      setAlert({ 
        open: true, 
        message: 'Error de conexión al guardar comentario', 
        severity: 'error' 
      })
    } finally {
      setUpdating(false)
    }
  }
  
  // Clave para localStorage
  const FILAS_VISIBLES_CACHE_KEY = 'planificacion_filas_visibles'
  // Migración única: usuarios con caché previo a las filas Tecno Super pueden no tenerlas guardadas.
  // No repetir tras la primera corrida para no pisar una preferencia válida del usuario (ej. ocultar una fila).
  const FILAS_VISIBLES_MIGRATION_KEY = 'planificacion_filas_visibles_migracion_tecno_v1'
  const FILAS_VISIBLES_MIGRATION_ELECTRIC_KEY =
    'planificacion_filas_visibles_migracion_electric_v1'

  const TECNO_FILAS_MIGRACION = [
    'Sell In Tecnosuper',
    'Sell In Sin Tecno Super',
    'Sell Out Tecnosuper',
  ]

  const getFilasVisiblesIniciales = () => {
    try {
      const cached = localStorage.getItem(FILAS_VISIBLES_CACHE_KEY)
      if (cached) {
        const filasCached = [...new Set(JSON.parse(cached).map(normalizarNombreFila))]
        const filasValidas = filasCached.filter((fila) => todasLasFilas.includes(fila))
        if (filasValidas.length > 0) {
          try {
            if (!localStorage.getItem(FILAS_VISIBLES_MIGRATION_KEY)) {
              localStorage.setItem(FILAS_VISIBLES_MIGRATION_KEY, '1')
              const faltantesTecno = TECNO_FILAS_MIGRACION.filter(
                (fila) => todasLasFilas.includes(fila) && !filasValidas.includes(fila),
              )
              if (faltantesTecno.length > 0) {
                return [...filasValidas, ...faltantesTecno]
              }
            }

            if (
              esCompaniaElectric(tipoNegocio) &&
              !localStorage.getItem(FILAS_VISIBLES_MIGRATION_ELECTRIC_KEY)
            ) {
              localStorage.setItem(FILAS_VISIBLES_MIGRATION_ELECTRIC_KEY, '1')
              const faltantesElectric = filasElectricLabels.filter(
                (fila) => !filasValidas.includes(fila),
              )
              if (faltantesElectric.length > 0) {
                return [...filasValidas, ...faltantesElectric]
              }
            }
          } catch {
            // ignorar errores de storage en migración
          }
          return filasValidas
        }
      }
    } catch (error) {
      console.error('Error al cargar filas visibles desde cache:', error)
    }
    return getFilasVisiblesPorDefecto(tipoNegocio)
  }
  
  // Estado para controlar qué filas están visibles (cargado desde cache o valores por defecto)
  const [filasVisibles, setFilasVisibles] = useState(getFilasVisiblesIniciales)

  useEffect(() => {
    setFilasVisibles((prev) => {
      const filasValidas = [...new Set(prev.map(normalizarNombreFila))].filter((fila) =>
        todasLasFilas.includes(fila),
      )

      if (esCompaniaElectric(tipoNegocio)) {
        const faltantesElectric = filasElectricLabels.filter(
          (fila) => !filasValidas.includes(fila),
        )
        if (faltantesElectric.length > 0) {
          return [...filasValidas, ...faltantesElectric]
        }
      }

      return filasValidas.length > 0
        ? filasValidas
        : getFilasVisiblesPorDefecto(tipoNegocio)
    })
  }, [tipoNegocio, todasLasFilas, filasElectricLabels])

  useEffect(() => {
    if (mostrarKardexTecnoSuper !== true) return
    setFilasVisibles((prev) => {
      const filasValidas = [...new Set(prev.map(normalizarNombreFila))].filter((fila) =>
        todasLasFilas.includes(fila),
      )
      const faltantesKardex = filasElectricLabels.filter(
        (fila) => !filasValidas.includes(fila),
      )
      if (faltantesKardex.length === 0) return prev
      return [...filasValidas, ...faltantesKardex]
    })
  }, [mostrarKardexTecnoSuper, todasLasFilas, filasElectricLabels])
  
  // Guardar en cache cuando cambien las filas visibles
  useEffect(() => {
    try {
      localStorage.setItem(FILAS_VISIBLES_CACHE_KEY, JSON.stringify(filasVisibles))
    } catch (error) {
      console.error('Error al guardar filas visibles en cache:', error)
    }
  }, [filasVisibles])
  
  // Handler para cambiar las filas visibles
  const handleFilasVisiblesChange = (event) => {
    const {
      target: { value },
    } = event
    const nuevasFilas = typeof value === 'string' ? value.split(',') : value
    setFilasVisibles(nuevasFilas)
  }

  useEffect(() => {
    async function fetchInventarioComentarios() {
      const response = await getInventarioComentarios(modeloPlaneacion, escenario)
      if (response && response.comentarios) {
        setComentarios(response.comentarios)
      }
    }
    async function fetchInclusionLlegadasModelo() {
      console.log('modeloPlaneacion', modeloPlaneacion)
      const response = await getInclusionLlegadasModelo({modelo_planeacion: modeloPlaneacion})
      if (response && response.detalles && response.detalles.inclusion_llegadas !== undefined) {
        const inclusionLlegadasValue = response.detalles.inclusion_llegadas
        setIsInclusionLlegadas(inclusionLlegadasValue)
      } else {
        setIsInclusionLlegadas(0)
      }
    } 
    // Solo llamar a fetchInventarioComentarios si hay modeloPlaneacion válido
    if (modeloPlaneacion) {
      fetchInventarioComentarios()
    }
    // Solo llamar a fetchInclusionLlegadasModelo si hay modeloPlaneacion válido
    // Esto evita llamadas innecesarias cuando modeloPlaneacion es null (como en ResumenCompraDetalle)
    if (modeloPlaneacion) {
      fetchInclusionLlegadasModelo()
    } else {
      // Si no hay modeloPlaneacion, establecer el valor por defecto
      setIsInclusionLlegadas(0)
    }
  }, [modeloPlaneacion, escenario])

  // useEffect para hacer scroll al mes actual - 1
  useEffect(() => {
    // Esperar a que la tabla se renderice completamente
    const timer = setTimeout(() => {
      const tableContainer = document.querySelector('.MuiTableContainer-root')
      
      if (tableContainer && mesesColumnas.length > 0) {
        const fechaActual = new Date()
        const mesActual = fechaActual.getMonth() + 1 // getMonth() devuelve 0-11
        const añoActual = fechaActual.getFullYear()
        
        // Calcular el mes objetivo (mes actual - 1)
        let mesObjetivo = mesActual - 1
        let añoObjetivo = añoActual
        
        if (mesObjetivo === 0) {
          mesObjetivo = 12
          añoObjetivo = añoActual - 1
        }
        
        let indiceColumna = -1
        
        if (tipoPeriodo === 'mes') {
          const nombresMeses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
          const nombreMesObjetivo = nombresMeses[mesObjetivo - 1]
          const añoCorto = añoObjetivo.toString().slice(-2)
          const mesColumnaObjetivo = `${nombreMesObjetivo}-${añoCorto}`
          
          indiceColumna = mesesColumnas.indexOf(mesColumnaObjetivo)
        } else if (tipoPeriodo === 'semana') {
          // Para semanas, buscar la primera semana del mes objetivo
          const getWeekNumber = (date) => {
            const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
            const dayNum = d.getUTCDay() || 7
            d.setUTCDate(d.getUTCDate() + 4 - dayNum)
            const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
            return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
          }
          
          const primerDiaMesObjetivo = new Date(añoObjetivo, mesObjetivo - 1, 1)
          const semanaObjetivo = getWeekNumber(primerDiaMesObjetivo)
          const semanaColumnaObjetivo = `${semanaObjetivo.toString().padStart(2, '0')}-${añoObjetivo}`
          
          indiceColumna = mesesColumnas.indexOf(semanaColumnaObjetivo)
        }
        
        if (indiceColumna !== -1) {
          // La primera columna es "Tipo de dato" que tiene 200px
          // Las columnas de meses tienen 120px cada una
          const anchoColumnaMes = 120
          const scrollPosition = indiceColumna * anchoColumnaMes
          
          tableContainer.scrollLeft = scrollPosition
        }
      }
    }, 100) // Pequeño delay para asegurar que la tabla esté renderizada
    
    return () => clearTimeout(timer)
  }, [mesesColumnas, tipoPeriodo])

  // Función para determinar si una celda es editable
  const isCellEditable = (tipoDato, mesColumna) => {
    // Si readOnly está activado, ninguna celda es editable
    if (readOnly) {
      return false
    }
    const fechaActual = new Date()
    const mesActual = fechaActual.getMonth() + 1
    const añoActual = fechaActual.getFullYear()
    
    // Para Sell In -- Plan Ventas y Sell In Tecnosuper: editable desde mes actual en adelante
    if (tipoDato === 'Sell In -- Plan Ventas' || tipoDato === 'Sell In Tecnosuper') {
      if (tipoPeriodo === 'mes') {
        const nombresMeses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
        const [nombreMesCol, añoColStr] = mesColumna.split('-')
        const mesCol = nombresMeses.indexOf(nombreMesCol) + 1
        const añoCol = parseInt(`20${añoColStr}`, 10)
        
        // Editable a partir del mes actual (inclusive)
        return añoCol > añoActual || (añoCol === añoActual && mesCol >= mesActual)
      } if (tipoPeriodo === 'semana') {
        const [semanaStr, añoStr] = mesColumna.split('-')
        const semanaCol = parseInt(semanaStr, 10)
        const añoCol = parseInt(añoStr, 10)
        
        const getWeekNumber = (date) => {
          const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
          const dayNum = d.getUTCDay() || 7
          d.setUTCDate(d.getUTCDate() + 4 - dayNum)
          const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
          return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
        }
        
        const semanaActual = getWeekNumber(fechaActual)
        
        // Editable a partir de la semana actual
        return añoCol > añoActual || (añoCol === añoActual && semanaCol >= semanaActual)
      }
    }
    
    // Para Licitacion: editable en todos los períodos
    if (tipoDato === 'Licitacion') {
      return true
    }

    // Para Sell Out Tecnosuper: editable en todos los períodos
    if (tipoDato === 'Sell Out Tecnosuper') {
      return true
    }
    
    // Para Llegadas (Cont): 
    // - Si isInclusionLlegadas es 1, editable desde el mes actual
    // - Si isInclusionLlegadas es 0 o no tiene dato, editable a partir del mes de llegada
    if (tipoDato === 'Llegadas (Cont)') {
      if (isInclusionLlegadas === 1) {
        // Editable en todos los meses
        return true
      }
      
      // Comportamiento original: editable a partir del mes de llegada
      if (!mesLlegada) return false
        
        if (tipoPeriodo === 'mes') {
          const nombresMeses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
          const [nombreMesCol, añoColStr] = mesColumna.split('-')
          const mesCol = nombresMeses.indexOf(nombreMesCol) + 1
          const añoCol = parseInt(`20${añoColStr}`, 10)
          
          // Parsear directamente el string para evitar problemas con zonas horarias
          const [añoLlegadaStr, mesLlegadaStr] = mesLlegada.split('-')
          const mesLlegadaNum = parseInt(mesLlegadaStr, 10)
          const añoLlegadaNum = parseInt(añoLlegadaStr, 10)
          
          // Editable a partir del mes de llegada (inclusive)
          return añoCol > añoLlegadaNum || (añoCol === añoLlegadaNum && mesCol >= mesLlegadaNum)
        } if (tipoPeriodo === 'semana') {
          const [semanaStr, añoStr] = mesColumna.split('-')
          const semanaCol = parseInt(semanaStr, 10)
          const añoCol = parseInt(añoStr, 10)
          
          // Parsear directamente el string para evitar problemas con zonas horarias
          const [añoLlegadaStr, mesLlegadaStr] = mesLlegada.split('-')
          const mesLlegadaNum = parseInt(mesLlegadaStr, 10)
          const añoLlegadaNum = parseInt(añoLlegadaStr, 10)
          
          // Obtener todas las semanas del mes de llegada
          const getWeekNumber = (date) => {
            const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
            const dayNum = d.getUTCDay() || 7
            d.setUTCDate(d.getUTCDate() + 4 - dayNum)
            const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
            return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
          }
          
          const primerDiaMes = new Date(añoLlegadaNum, mesLlegadaNum - 1, 1)
          const primeraSemanaLlegada = getWeekNumber(primerDiaMes)
          
          // Editable a partir de la primera semana del mes de llegada
          return añoCol > añoLlegadaNum || (añoCol === añoLlegadaNum && semanaCol >= primeraSemanaLlegada)
        }
    }
    
    return false
  }

  // Agregar fila de comentarios a los datos de la tabla
  const tablaDataConComentarios = React.useMemo(() => {
    // Función para obtener el comentario de un mes específico
    const getComentarioForMes = (mesColumna) => {
      if (!comentarios || comentarios.length === 0) return ''
      
      const { mes, año } = getMesAnoFromColumn(mesColumna)
      const comentario = comentarios.find(c => c.mes_semana === mes && c.anio === año)
      return comentario ? comentario.comentario : ''
    }

    // Crear la fila de comentarios
    const filaComentarios = {
      tipoDato: 'Comentarios',
    }
    
    // Agregar comentarios para cada columna de mes
    mesesColumnas.forEach(mes => {
      filaComentarios[mes] = getComentarioForMes(mes)
    })
    
    // Insertar la fila de comentarios después de "Días Inventario objetivo final (PV)"
    const indexDiasInventario = tablaData.findIndex(row => row.tipoDato === 'Días Inventario objetivo final (PV)')
    
    if (indexDiasInventario !== -1) {
      const nuevaTablaData = [...tablaData]
      nuevaTablaData.splice(indexDiasInventario + 1, 0, filaComentarios)
      return nuevaTablaData
    }
    
    return [...tablaData, filaComentarios]
  }, [tablaData, comentarios, mesesColumnas, tipoPeriodo])
  
  // Filtrar los datos de la tabla basándose en las filas visibles
  const tablaDataFiltrada = tablaDataConComentarios.filter((row) => {
    const esFilaKardex = filasElectricLabels.includes(row.tipoDato)
    if (esFilaKardex && mostrarKardexTecnoSuper === true) return true
    if (esFilaKardex && mostrarKardexTecnoSuper === false) return false
    return filasVisibles.includes(row.tipoDato)
  })

  // Función para manejar el inicio de edición
  const handleEditStart = (tipoDato, mesColumna, value) => {
    if (isCellEditable(tipoDato, mesColumna)) {
      setEditingCell(`${tipoDato}-${mesColumna}`)
      setEditValue(value || '')
    }
  }

  // Función para cancelar edición
  const handleEditCancel = () => {
    setEditingCell(null)
    setEditValue('')
  }

  // Función para guardar edición
  const handleEditSave = async (tipoDato, mesColumna) => {
    if (!editValue || editValue === '' || Number.isNaN(Number(editValue))) {
      setAlert({ open: true, message: 'Por favor ingresa un valor numérico válido', severity: 'error' })
      return
    }

    // Validar que sea un número positivo
    const valorNumerico = parseInt(editValue, 10)
    if (valorNumerico < 0) {
      setAlert({ open: true, message: 'Por favor ingresa un valor positivo', severity: 'error' })
      return
    }

    setUpdating(true)
    try {
      const { mes, año } = getMesAnoFromColumn(mesColumna)
      
      if (tipoDato === 'Sell In -- Plan Ventas') {
        // Edición directa de Sell In
        const sellInData = {
          informacion: {
            modelo_planeacion: modeloPlaneacion,
            escenario,
            tipo_periodo: tipoPeriodo === 'mes' ? 'mes' : 'semana'
          },
          informacion_sell_in: [
            {
              anio: año,
              sell_in: valorNumerico,
              mes
            }
          ]
        }

        const response = await modificarSellInDirecto(sellInData)
        
        if (response && response.success) {
          setAlert({ open: true, message: response.message || 'Sell In modificado exitosamente', severity: 'success' })
          setEditingCell(null)
          setEditValue('')
          
          // Recargar los datos de la tabla
          if (onDataRefresh) {
            onDataRefresh()
          }
        } else {
          setAlert({ open: true, message: response?.message || 'Error al modificar Sell In', severity: 'error' })
        }
      } else if (tipoDato === 'Sell In Tecnosuper') {
        const payload = {
          modelo_planeacion: modeloPlaneacion,
          sell_in_tecnosuper: valorNumerico,
          tipo_periodo: tipoPeriodo === 'mes' ? 'mes' : 'semana',
          mes_semana: mes,
          anio: año,
          escenario,
        }
        const response = await modificarSellInTecnoSuper(payload)
        if (response && response.success) {
          setAlert({ open: true, message: response.message || 'Sell In Tecnosuper actualizado correctamente', severity: 'success' })
          setEditingCell(null)
          setEditValue('')
          if (onDataRefresh) {
            onDataRefresh()
          }
        } else {
          setAlert({ open: true, message: response?.message || 'Error al actualizar Sell In Tecnosuper', severity: 'error' })
        }
      } else if (tipoDato === 'Licitacion') {
        // Edición de Licitacion
        const licitacionData = {
          modelo_planeacion: modeloPlaneacion,
          licitacion: valorNumerico,
          tipo_periodo: tipoPeriodo === 'mes' ? 'mes' : 'semana',
          mes_semana: mes,
          anio: año
        }

        const response = await modificarLicitacion(licitacionData)
        
        if (response && response.success) {
          setAlert({ open: true, message: response.message || 'Licitación modificada exitosamente', severity: 'success' })
          setEditingCell(null)
          setEditValue('')
          
          // Recargar los datos de la tabla
          if (onDataRefresh) {
            onDataRefresh()
          }
        } else {
          setAlert({ open: true, message: response?.message || 'Error al modificar licitación', severity: 'error' })
        }
      } else if (tipoDato === 'Sell Out Tecnosuper') {
        const payload = {
          modelo_planeacion: modeloPlaneacion,
          sell_out_tecnosuper: valorNumerico,
          tipo_periodo: tipoPeriodo === 'mes' ? 'mes' : 'semana',
          mes_semana: mes,
          anio: año,
        }
        const response = await modificarSellOutTecnoSuper(payload)
        if (response && response.success) {
          setAlert({ open: true, message: response.message || 'Sell Out Tecnosuper actualizado correctamente', severity: 'success' })
          setEditingCell(null)
          setEditValue('')
          if (onDataRefresh) {
            onDataRefresh()
          }
        } else {
          setAlert({ open: true, message: response?.message || 'Error al actualizar Sell Out Tecnosuper', severity: 'error' })
        }
      } else if (tipoDato === 'Llegadas (Cont)') {
        // Edición de contenedores
        const contenedoresData = {
          modelo_planeacion: modeloPlaneacion,
          anio: año,
          mes,
          contenedores: valorNumerico,
          escenario,
          tipo_periodo: tipoPeriodo === 'mes' ? 'mes' : 'semana'
        }

        const response = await modificarContenedores(contenedoresData)
        
        if (response && response.success) {
          setAlert({ open: true, message: response.message, severity: 'success' })
          setEditingCell(null)
          setEditValue('')
          
          // Recargar los datos de la tabla
          if (onDataRefresh) {
            onDataRefresh()
          }
        } else {
          setAlert({ open: true, message: response?.message || 'Error al modificar contenedores', severity: 'error' })
        }
      }
    } catch (error) {
      console.error('Error modificando datos:', error)
      setAlert({ open: true, message: 'Error de conexión al modificar datos', severity: 'error' })
    } finally {
      setUpdating(false)
    }
  }

  // Función para cerrar alerta
  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false })
  }

  const handleOpenMultipleModal = (tipoDato) => {
    setMultipleTipoDato(tipoDato)
    setMultipleValue('')
    setMultiplePeriodos(getPeriodosMesActualHastaSiguienteAnio())
    setOpenMultipleModal(true)
  }

  const handleCloseMultipleModal = () => {
    setOpenMultipleModal(false)
    setMultipleTipoDato('')
    setMultipleValue('')
    setMultiplePeriodos([])
  }

  const handleTogglePeriodoMultiple = (index) => {
    setMultiplePeriodos((prev) =>
      prev.map((p, i) => (i === index ? { ...p, selected: !p.selected } : p))
    )
  }

  const handleSelectAllPeriodos = (checked) => {
    setMultiplePeriodos((prev) => prev.map((p) => ({ ...p, selected: checked })))
  }

  const handleConfirmarMultiple = () => {
    const valor = Number(multipleValue)
    if (multipleValue === '' || Number.isNaN(valor) || valor < 0) {
      setAlert({ open: true, message: 'Ingrese un valor numérico válido', severity: 'warning' })
      return
    }
    const seleccionados = multiplePeriodos.filter((p) => p.selected)
    if (seleccionados.length === 0) {
      setAlert({ open: true, message: 'Seleccione al menos un periodo', severity: 'warning' })
      return
    }
    setOpenConfirmacionMultiple(true)
  }

  const handleEnviarMultiple = async () => {
    setOpenConfirmacionMultiple(false)
    const config = MULTIPLE_EDIT_CONFIG[multipleTipoDato]
    if (!config) return
    const valor = Number(multipleValue)
    const periodos = multiplePeriodos
      .filter((p) => p.selected)
      .map((p) => ({ anio: p.anio, mes_semana: p.mes_semana }))
    setSubmittingMultiple(true)
    try {
      const response = await config.service({
        [config.valueKey]: valor,
        tipo_periodo: 'mes',
        escenario,
        modelo_planeacion: modeloPlaneacion,
        periodos,
      })
      if (response?.success) {
        handleCloseMultipleModal()
        setAlert({ open: true, message: response.message || `${config.title} aplicado correctamente`, severity: 'success' })
        onDataRefresh?.()
      } else {
        setAlert({ open: true, message: response?.message || `Error al aplicar ${config.label} múltiple`, severity: 'error' })
      }
    } catch (err) {
      setAlert({ open: true, message: err?.message || 'Error de conexión', severity: 'error' })
    } finally {
      setSubmittingMultiple(false)
    }
  }

  // Función para determinar el color del tipo de dato
  const getTipoDatoColor = (tipoDato) => {
    switch (tipoDato) {
      case 'Inventario Inicial en sitio':
        return '#E3F2FD' // Azul claro
      case 'Pedido a proveedor':
        return '#FFF2CC' // Amarillo claro
      case 'Llegadas (Und)':
        return '#FFF2CC' // Amarillo claro
      case 'Llegadas (Cont)':
        return '#FFF2CC' // Amarillo claro
      case 'Sell In -- Plan Ventas':
        return '#E8F5E8' // Verde claro
      case 'Sell In Tecnosuper':
        return '#E8F5E8' // Verde claro (mismo que Sell In)
      case 'Sell In Sin Tecno Super':
        return '#E8F5E8' // Verde claro (mismo que Sell In)
      case 'Licitacion':
        return '#E8F5E8' // Verde claro (mismo que Sell In)
      case 'Sell Out Tecnosuper':
        return '#f9f9f9' // Gris claro (mismo que Sell Out)
      case 'Sell Out':
        return '#f9f9f9' // Gris claro
      case 'Rolling':
        return '#FFF2CC' // Amarillo claro
      case 'Inventario final UN (PV) teórico':
        return '#E3F2FD' // Azul claro
      case 'Inventario final UN (PV) real':
        return '#E3F2FD' // Azul claro
      case 'Días Inventario objetivo final (PV)':
        return '#f9f9f9' // Gris claro
      case 'Comentarios':
        return '#FFF9E6' // Amarillo muy claro
      case 'Inventario Inicial en sitio Tecnosuper + Auteco':
      case 'Inventario Inicial en sitio Tecnosuper':
      case 'Inventario final Tecnosuper + Auteco':
      case 'Inventario final Tecnosuper':
        return '#E3F2FD' // Azul claro
      case 'Facturación Tecnosuper + Auteco':
        return '#FCE4EC' // Rosa claro
      default:
        return '#f9f9f9' // Gris claro por defecto
    }
  }

  // Función para determinar si una celda debe tener estilos especiales (negrita y más grande)
  const shouldHighlightLlegadasUnd = (tipoDato, mesColumna) => {
    if (tipoDato !== 'Llegadas (Und)' || !mesLlegada) return false
    
    if (tipoPeriodo === 'mes') {
      const nombresMeses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
      const [nombreMes, añoCorto] = mesColumna.split('-')
      const mesCol = nombresMeses.indexOf(nombreMes) + 1
      const añoCol = parseInt(`20${añoCorto}`, 10)
      
      // Parsear directamente el string para evitar problemas con zonas horarias
      const [añoLlegadaStr, mesLlegadaStr] = mesLlegada.split('-')
      const mesLlegadaNum = parseInt(mesLlegadaStr, 10)
      const añoLlegadaNum = parseInt(añoLlegadaStr, 10)
      
      return mesCol === mesLlegadaNum && añoCol === añoLlegadaNum
    } if (tipoPeriodo === 'semana') {
      const [semanaStr, añoStr] = mesColumna.split('-')
      const semanaCol = parseInt(semanaStr, 10)
      const añoCol = parseInt(añoStr, 10)
      
      // Parsear directamente el string para evitar problemas con zonas horarias
      const [añoLlegadaStr, mesLlegadaStr] = mesLlegada.split('-')
      const mesLlegadaNum = parseInt(mesLlegadaStr, 10)
      const añoLlegadaNum = parseInt(añoLlegadaStr, 10)
      
      // Obtener todas las semanas del mes de llegada
      const getWeekNumber = (date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
        const dayNum = d.getUTCDay() || 7
        d.setUTCDate(d.getUTCDate() + 4 - dayNum)
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
        return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
      }
      
      const primerDiaMes = new Date(añoLlegadaNum, mesLlegadaNum - 1, 1)
      const ultimoDiaMes = new Date(añoLlegadaNum, mesLlegadaNum, 0)
      const primeraSemana = getWeekNumber(primerDiaMes)
      const ultimaSemana = getWeekNumber(ultimoDiaMes)
      
      return añoCol === añoLlegadaNum && semanaCol >= primeraSemana && semanaCol <= ultimaSemana
    }
    
    return false
  }

  // Función para determinar el color de la columna
  const getColumnColor = (columna) => {
    const fechaActual = new Date()
    const mesActual = fechaActual.getMonth() + 1 // getMonth() devuelve 0-11
    const añoActual = fechaActual.getFullYear()
    
    if (tipoPeriodo === 'mes') {
      // Parsear el formato "ene-25", "feb-25", etc.
      const nombresMeses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
      const [nombreMes, añoCorto] = columna.split('-')
      const mesColumna = nombresMeses.indexOf(nombreMes) + 1
      const añoColumna = parseInt(`20${añoCorto}`, 10)
      
      // Verificar si es el mes de llegada
      if (mesLlegada) {
        // Parsear directamente el string para evitar problemas con zonas horarias
        // El formato esperado es "YYYY-MM-DD"
        const [añoLlegadaStr, mesLlegadaStr] = mesLlegada.split('-')
        const mesLlegadaNum = parseInt(mesLlegadaStr, 10)
        const añoLlegadaNum = parseInt(añoLlegadaStr, 10)
        
        if (mesColumna === mesLlegadaNum && añoColumna === añoLlegadaNum) {
          return '#E1D5E7 !important' // Morado suave para mes de llegada
        }
      }
      
      // Verificar si es el mes actual
      if (mesColumna === mesActual && añoColumna === añoActual) {
        return '#E1D5E7 !important' // Morado suave para mes actual
      }
      
      // Meses futuros
      if (añoColumna > añoActual || (añoColumna === añoActual && mesColumna > mesActual)) {
        return '#E3F2FD !important' // Azul suave para meses futuros
      }
      
    } else if (tipoPeriodo === 'semana') {
      // Parsear el formato "01-2025", "02-2025", etc.
      const [semanaStr, añoStr] = columna.split('-')
      const semanaColumna = parseInt(semanaStr, 10)
      const añoColumna = parseInt(añoStr, 10)
      
      // Obtener semana actual
      const getWeekNumber = (date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
        const dayNum = d.getUTCDay() || 7
        d.setUTCDate(d.getUTCDate() + 4 - dayNum)
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
        return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
      }
      
      const semanaActual = getWeekNumber(fechaActual)
      
      // Verificar si es semana del mes de llegada
      if (mesLlegada) {
        // Parsear directamente el string para evitar problemas con zonas horarias
        const [añoLlegadaStr, mesLlegadaStr] = mesLlegada.split('-')
        const mesLlegadaNum = parseInt(mesLlegadaStr, 10)
        const añoLlegadaNum = parseInt(añoLlegadaStr, 10)
        
        // Obtener todas las semanas del mes de llegada
        const primerDiaMes = new Date(añoLlegadaNum, mesLlegadaNum - 1, 1)
        const ultimoDiaMes = new Date(añoLlegadaNum, mesLlegadaNum, 0)
        const primeraSemana = getWeekNumber(primerDiaMes)
        const ultimaSemana = getWeekNumber(ultimoDiaMes)
        
        if (añoColumna === añoLlegadaNum && semanaColumna >= primeraSemana && semanaColumna <= ultimaSemana) {
          return '#E1D5E7' // Morado suave para semanas del mes de llegada
        }
      }
      
      // Verificar si es semana del mes actual
      const primerDiaMesActual = new Date(añoActual, mesActual - 1, 1)
      const ultimoDiaMesActual = new Date(añoActual, mesActual, 0)
      const primeraSemanaActual = getWeekNumber(primerDiaMesActual)
      const ultimaSemanaActual = getWeekNumber(ultimoDiaMesActual)
      
      if (añoColumna === añoActual && semanaColumna >= primeraSemanaActual && semanaColumna <= ultimaSemanaActual) {
        return '#E1D5E7' // Morado suave para semanas del mes actual
      }
      
      // Semanas futuras
      if (añoColumna > añoActual || (añoColumna === añoActual && semanaColumna > semanaActual)) {
        return '#E3F2FD' // Azul suave para semanas futuras
      }
    }
    
    return '#f5f5f5' // Color por defecto
  }

  // Resaltado contextual según el color base de la fila
  const getSpecialHighlightStyle = (tipoDato) => {
    const colorBase = (getTipoDatoColor(tipoDato) || '').replace(' !important', '')

    switch (colorBase) {
      case '#E8F5E8': // Verde
        return { backgroundColor: '#c8e6c9', color: '#1b5e20' }
      case '#E3F2FD': // Azul
        return { backgroundColor: '#bbdefb', color: '#0d47a1' }
      case '#FFF2CC': // Amarillo
        return { backgroundColor: '#ffe082', color: '#7a5c00' }
      case '#f9f9f9': // Gris
        return { backgroundColor: '#e0e0e0', color: '#37474f' }
      default:
        return { backgroundColor: '#c8e6c9', color: '#1b5e20' }
    }
  }

  // Crear columnas dinámicamente
  const columns = [
    {
      accessorKey: 'tipoDato',
      header: 'Tipo de dato',
      size: 200,
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: '#f5f5f5 !important',
          color: 'black',
          fontWeight: 'bold',
          borderRight: '1px solid #ddd',
          position: 'sticky',
          left: 0,
          zIndex: 1,
        },
      },
      muiTableBodyCellProps: ({ row }) => ({
        sx: {
          backgroundColor: getTipoDatoColor(row.getValue('tipoDato')),
          fontWeight: 'bold',
          borderRight: '1px solid #ddd',
          position: 'sticky',
          left: 0,
          zIndex: 1,
        },
      }),
      Cell: ({ row }) => {
        const tipoDato = row.getValue('tipoDato')
        const tieneBotonLlegadas = tipoDato === 'Llegadas (Cont)'
        const tieneBotonMultiple = Boolean(MULTIPLE_EDIT_CONFIG[tipoDato]) && tipoPeriodo === 'mes'
        const tieneBoton = tieneBotonLlegadas || tieneBotonMultiple
        const labelTipoDato = tipoDato

        const handleButtonClick = () => {
          if (MULTIPLE_EDIT_CONFIG[tipoDato] && tipoPeriodo === 'mes') {
            handleOpenMultipleModal(tipoDato)
          } else if (tipoDato === 'Sell In -- Plan Ventas' && onSellInButtonClick) {
            onSellInButtonClick()
          } else if (tipoDato === 'Llegadas (Cont)' && onLlegadasButtonClick) {
            onLlegadasButtonClick()
          }
        }

        // En escenarios de solo lectura (actualizados) ocultamos los botones de
        // edición, pero el de "Llegadas (Cont)" se mantiene habilitado únicamente
        // para abrir el modal en modo consulta.
        const mostrarBoton = tieneBotonLlegadas || (tieneBoton && !readOnly)

        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {mostrarBoton && (
              <Button
                size="small"
                onClick={handleButtonClick}
                sx={{
                  minWidth: '20px',
                  width: '20px',
                  height: '20px',
                  mr: 1,
                  fontSize: '12px',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                }}
              >
                +
              </Button>
            )}
            {labelTipoDato}
          </Box>
        )
      },
    },
    ...mesesColumnas.map((mes) => {
      // Convertir el formato del header para mostrar el año completo
      let headerText = mes;
      if (tipoPeriodo === 'mes') {
        const [nombreMes, añoCorto] = mes.split('-');
        const añoCompleto = `20${añoCorto}`;
        headerText = `${nombreMes}-${añoCompleto}`;
      } else if (tipoPeriodo === 'semana') {
        const [semana, año] = mes.split('-');
        headerText = `${semana}-${año}`;
      }
      
      return {
        accessorKey: mes,
        header: headerText,
        size: 120,
        muiTableHeadCellProps: {
          sx: {
            backgroundColor: getColumnColor(mes),
            color: 'black',
            fontWeight: 'bold',
            borderRight: '1px solid #ddd',
            textAlign: 'center',
          },
        },
        muiTableBodyCellProps: ({ row, cell }) => {
          const tipoDato = row.getValue('tipoDato')
          const isHighlighted = shouldHighlightLlegadasUnd(tipoDato, mes)
          const isSpecialHighlighted = Boolean(row.original?.__highlightFlags?.[mes])
          const specialHighlightStyle = getSpecialHighlightStyle(tipoDato)
          const value = cell.getValue()
          
          // Aplicar fondo rojo pastel si es "Días Inventario objetivo final (PV)" y el valor es menor que nivelServicio
          let backgroundColor
          if (tipoDato === 'Días Inventario objetivo final (PV)') {
            const valorNumerico = parseFloat(value)
            const nivelServicioNumerico = nivelServicio !== undefined && nivelServicio !== null && nivelServicio !== '' 
              ? parseFloat(nivelServicio) 
              : null
            
            if (nivelServicioNumerico !== null && !isNaN(valorNumerico) && !isNaN(nivelServicioNumerico) && valorNumerico < nivelServicioNumerico) {
              backgroundColor = '#ffb6c1b5' // Rojo pastel
            }
          }

          if (!backgroundColor && isSpecialHighlighted) {
            backgroundColor = specialHighlightStyle.backgroundColor
          }
          
          return {
            sx: {
              textAlign: 'center',
              borderRight: isHighlighted ? '3px solid #4c75aa' : '1px solid #ddd',
              borderTop: isHighlighted ? '3px solid #4c75aa' : '1px solid #ddd',
              borderBottom: isHighlighted ? '3px solid #4c75aa' : '1px solid #ddd',
              borderLeft: isHighlighted ? '3px solid #4c75aa' : '1px solid #ddd',
              backgroundColor,
            },
          }
        },
        Cell: ({ cell, row }) => {
          const value = cell.getValue()
          const tipoDato = row.getValue('tipoDato')
          const mesColumna = mes
          const cellKey = `${tipoDato}-${mesColumna}`
          const isEditable = isCellEditable(tipoDato, mesColumna)
          const isCurrentlyEditing = editingCell === cellKey

          // Función para formatear números con máximo 2 decimales
          // const formatNumber = (val) => {
          //   if (val === null || val === undefined || val === '') return ''
          //   const num = parseFloat(val)
          //   if (Number.isNaN(num)) return val
          //   return parseFloat(num.toFixed(2)).toString()
          // }

          if (isCurrentlyEditing) {
            return (
              <TextField
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                size="small"
                type="number"
                disabled={updating}
                sx={{ 
                  width: '100%',
                  '& .MuiInputBase-input': {
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    textAlign: 'center'
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#1976d2',
                    },
                  }
                }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleEditSave(tipoDato, mesColumna)
                  } else if (e.key === 'Escape') {
                    handleEditCancel()
                  } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    // Permitir el movimiento del cursor con las teclas de flecha sin guardar
                    // No hacer nada adicional
                  }
                }}
                onBlur={() => {
                  // Solo cancelar edición al perder el foco, no guardar automáticamente
                  handleEditCancel()
                }}
                InputProps={{
                  sx: {
                    height: '32px'
                  }
                }}
              />
            )
          }

          if (isEditable) {
            const isHighlighted = shouldHighlightLlegadasUnd(tipoDato, mesColumna)
            const isSpecialHighlighted = Boolean(row.original?.__highlightFlags?.[mesColumna])
            const specialHighlightStyle = getSpecialHighlightStyle(tipoDato)
            const shouldEmphasizeValue = isHighlighted || isSpecialHighlighted
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  padding: '2px 4px',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#f0f0f0'
                  }
                }}
                onClick={() => handleEditStart(tipoDato, mesColumna, value)}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: shouldEmphasizeValue ? 'bold' : 'normal',
                    fontSize: shouldEmphasizeValue ? '1.1rem' : 'inherit',
                    color: isSpecialHighlighted ? specialHighlightStyle.color : 'inherit',
                  }}
                >
                  {formatNumber(value)}
                </Box>
                <EditIcon fontSize="small" sx={{ opacity: 0.6 }} />
              </Box>
            )
          }
          
          // Para comentarios, mostrar campo editable con botón +
          if (tipoDato === 'Comentarios') {
            const isEditingThisComentario = editingComentario === mesColumna

            if (isEditingThisComentario) {
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                  <TextField
                    value={comentarioValue}
                    onChange={(e) => setComentarioValue(e.target.value)}
                    size="small"
                    multiline
                    rows={2}
                    disabled={updating}
                    placeholder="Escribe un comentario..."
                    sx={{ 
                      width: '100%',
                      '& .MuiInputBase-input': {
                        padding: '4px 8px',
                        fontSize: '0.75rem',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#1976d2',
                        },
                      }
                    }}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        handleSaveComentario(mesColumna)
                      } else if (e.key === 'Escape') {
                        handleEditComentarioCancel()
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleSaveComentario(mesColumna)}
                      disabled={updating}
                      sx={{ 
                        minWidth: '60px',
                        fontSize: '0.7rem',
                        py: 0.3,
                        px: 1
                      }}
                    >
                      Guardar
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleEditComentarioCancel}
                      disabled={updating}
                      sx={{ 
                        minWidth: '60px',
                        fontSize: '0.7rem',
                        py: 0.3,
                        px: 1
                      }}
                    >
                      Cancelar
                    </Button>
                  </Box>
                </Box>
              )
            }

            return (
              <Tooltip 
                title={value || ''}
                placement="top"
                arrow
                enterDelay={300}
                disableHoverListener={!value}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#333',
                      '& .MuiTooltip-arrow': {
                        color: '#333',
                      },
                      fontSize: '0.875rem',
                      maxWidth: '400px',
                      padding: '10px 15px',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word'
                    }
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 6px',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    minHeight: '60px',
                    '&:hover': {
                      backgroundColor: '#f0f0f0'
                    }
                  }}
                  onClick={() => readOnly ? null : handleEditComentarioStart(mesColumna, value)}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textAlign: 'left',
                      lineHeight: '1.4',
                      wordBreak: 'break-word'
                    }}
                  >
                    {value || ''}
                  </Box>
                </Box>
              </Tooltip>
            )
          }
          
          // Verificar si esta celda debe tener estilos especiales
          const isHighlighted = shouldHighlightLlegadasUnd(tipoDato, mesColumna)
          const isSpecialHighlighted = Boolean(row.original?.__highlightFlags?.[mesColumna])
          const specialHighlightStyle = getSpecialHighlightStyle(tipoDato)
          
          if(tipoDato === 'Días Inventario objetivo final (PV)') {
            return formatNumber(value)
          }
          
          const formattedValue = formatNumber(parseInt(value, 10))
          
          // Si la celda requiere resaltado visual, aplicar estilos especiales
          if (isHighlighted || isSpecialHighlighted) {
            return (
              <Box
                component="span"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  color: isSpecialHighlighted ? specialHighlightStyle.color : 'inherit',
                  display: 'inline-block',
                }}
              >
                {formattedValue}
              </Box>
            )
          }
          
          return formattedValue
        },
      }
    }),
    
  ]

  const table = useMaterialReactTable({
    columns,
    data: tablaDataFiltrada,
    enablePagination: false,
    enableSorting: false,
    enableResizing: true,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableToolbarInternalActions: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableRowActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    density: 'compact',
    muiTableContainerProps: {
      sx: {
        overflowX: 'auto',
        overflowY: 'visible',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: getTipoDatoColor(row.original.tipoDato),
        '&:hover': {
          backgroundColor: '#f9f9f9 !important',
        },
      },
    }),
    muiTableProps: {
      sx: {
        tableLayout: 'fixed',
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 0,
        border: '1px solid #ddd',
      },
    },
  })

  const mostrarBarraPeriodoBias = typeof onTipoPeriodoChange === 'function' && Array.isArray(tiposPeriodoDisponibles)

  return (
    <>
      {/* Selector de período (Mes/Semana), Bias/Mape y filas visibles */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {mostrarBarraPeriodoBias ? (
            <Select
              value={tipoPeriodo}
              onChange={onTipoPeriodoChange}
              size="small"
              sx={{ minWidth: 120 }}
            >
              {tiposPeriodoDisponibles.map((periodo) => (
                <MenuItem key={periodo.value} value={periodo.value}>
                  {periodo.label}
                </MenuItem>
              ))}
            </Select>
          ) : null}
          {metricasBiasMape ? (
            <>
              <BiasMapeMiniCard
                title="Bias"
                unidades={metricasBiasMape.bias_unidades}
                ratio={metricasBiasMape.bias}
              />
              <BiasMapeMiniCard
                title="Mape"
                variant="mape"
                unidades={metricasBiasMape.mape_unidades}
                ratio={metricasBiasMape.mape}
              />
            </>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
          {fobLlegadasUnidadesTotales != null ? (
            <Card
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'rgba(46, 125, 50, 0.22)',
                borderRadius: 2,
                background: 'linear-gradient(145deg, #e8f5e9 0%, #dcedc8 100%)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
              }}
            >
              <CardContent
                sx={{
                  py: 1.25,
                  px: 1.75,
                  textAlign: 'center',
                  '&:last-child': { pb: 1.25 },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#2e7d32',
                    mb: 0.75,
                    lineHeight: 1.2,
                  }}
                >
                  {mesLlegada
                    ? `COMPRA - ${new Date(
                        Number(mesLlegada.split('-')[0]),
                        Number(mesLlegada.split('-')[1]) - 1,
                        1
                      ).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })}`
                    : 'COMPRA'}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.125rem',
                    fontVariantNumeric: 'tabular-nums',
                    color: 'rgba(0, 0, 0, 0.87)',
                    lineHeight: 1.2,
                  }}
                >
                  ${formatDecimalMax2(fobLlegadasUnidadesTotales)}
                </Typography>
              </CardContent>
            </Card>
          ) : null}
        <FormControl sx={{ minWidth: 120, maxWidth: 600 }}>
          <InputLabel id="filas-visibles-label">Filas visibles</InputLabel>
          <Select
            labelId="filas-visibles-label"
            id="filas-visibles-select"
            multiple
            size="small"
            value={filasVisibles}
            onChange={handleFilasVisiblesChange}
            input={<OutlinedInput label="Filas visibles" />}
            renderValue={(selected) => `${selected.length} / ${todasLasFilas.length}`}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 400,
                },
              },
            }}
          >
            {todasLasFilas.map((fila) => {
              const labelFila = fila
              return (
                <MenuItem key={fila} value={fila}>
                  <Checkbox checked={filasVisibles.indexOf(fila) > -1} />
                  <ListItemText primary={labelFila} />
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
        </Box>
      </Box>
      
      <MaterialReactTable table={table} />

      {/* Modal de edición masiva (múltiple) por periodos */}
      <Dialog open={openMultipleModal} onClose={handleCloseMultipleModal} maxWidth="sm" fullWidth>
        <DialogTitle>{MULTIPLE_EDIT_CONFIG[multipleTipoDato]?.title || 'Edición múltiple'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Valor"
              type="number"
              value={multipleValue}
              onChange={(e) => setMultipleValue(e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              size="small"
            />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography component="span" variant="body2" color="text.secondary">
                  Meses
                </Typography>
                <div>
                  <Button  size="small" onClick={() => handleSelectAllPeriodos(true)}>
                    Todos
                  </Button>
                  <Button size="small" onClick={() => handleSelectAllPeriodos(false)}>
                    Ninguno
                  </Button>
                </div>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {multiplePeriodos.map((p, index) => (
                  <FormControlLabel
                    key={`${p.anio}-${p.mes_semana}`}
                    control={
                      <Checkbox
                        checked={p.selected}
                        onChange={() => handleTogglePeriodoMultiple(index)}
                      />
                    }
                    label={p.label}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMultipleModal}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmarMultiple} disabled={submittingMultiple}>
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación antes de enviar la edición masiva */}
      <Dialog open={openConfirmacionMultiple} onClose={() => setOpenConfirmacionMultiple(false)}>
        <DialogTitle>Confirmar envío</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Desea aplicar el valor de {MULTIPLE_EDIT_CONFIG[multipleTipoDato]?.label || 'este campo'} a los periodos seleccionados?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmacionMultiple(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleEnviarMultiple} disabled={submittingMultiple} color="primary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar para notificaciones */}
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default PlanificacionTable 